import React, { useCallback, useMemo } from 'react';

import { RouteComponentProps } from 'react-router-dom';

import * as Yup from 'yup';

import {
  mergeApplication,
  selectCreditApplication,
} from 'state/creditApplication';
import FmcForm from 'components/FmcForm';
import UnifiedPrequalApplicationReviewView from './UnifiedPrequalApplicationReviewView';
import useNavigationHelpers from 'hooks/useNavigationHelpers';
import useTranslate from 'hooks/useTranslate';
import useApplyUnifiedPrequalApplication from './applyPrequalApplication';
import { useDispatch, useSelector } from 'react-redux';
import catchOnly from 'utils/catchOnly';
import { NonClientError } from 'hooks/requests/fetchUtils';
import { pick } from 'ramda';
import { PrequalificationApplicationError } from 'hooks/requests/prequalificationApplication';
import {
  isMoney,
  composeSchema,
  isReferralCode,
  isLoyaltyNumber,
  isStoreNumber,
} from 'utils/validations';
import useFieldCustomization from 'hooks/useFieldCustomization';

export interface FormValues {
  income: string | null;
  consent: boolean;
  referralCode: string | null;
  storeNumber: string | null;
  intendedPurchaseAmount: string;
  reCaptchaToken: string | null;
}

type PropTypes = RouteComponentProps;

const UnifiedPrequalApplicationReview: React.FC<PropTypes> = () => {
  const { push } = useNavigationHelpers();
  const t = useTranslate();
  const dispatch = useDispatch();

  const applyUnifiedPrequalApplication = useApplyUnifiedPrequalApplication();
  const { isRequired } = useFieldCustomization();
  const schema = useMemo(
    () =>
      Yup.object().shape({
        income: composeSchema(
          Yup.string()
            .label(t('formLabel.annualIncome'))
            .nullable()
            .concat(isMoney),
          isRequired('annualIncome') ? Yup.string().required() : null,
        ),
        consent: Yup.boolean()
          .test('isChecked', 'must consent', consent => consent)
          .default(false),
        referralCode: composeSchema(
          Yup.string()
            .label(t('formLabel.referralCode'))
            .nullable()
            .concat(isReferralCode),
          isRequired('referralCode') ? Yup.string().required() : null,
        ),
        storeNumber: composeSchema(
          Yup.string()
            .label(t('formLabel.storeNumber'))
            .nullable()
            .concat(isStoreNumber),
          isRequired('storeNumber') ? Yup.string().required() : null,
        ),
        intendedPurchaseAmount: composeSchema(
          Yup.string()
            .label(t('formLabel.intendedPurchaseAmount'))
            .concat(isMoney)
            .required(),
        ),
        loyaltyNumber: composeSchema(
          Yup.string()
            .label(t('formLabel.loyaltyNumber'))
            .nullable()
            .concat(isLoyaltyNumber()),
          isRequired('loyaltyNumber') ? Yup.string().required() : null,
        ),
        reCaptchaToken: Yup.string().required(),
      }),
    [t, isRequired],
  );

  const onSubmit = useCallback(
    async (rawValues: FormValues) => {
      const castValues = schema.cast(rawValues);

      try {
        dispatch(mergeApplication(castValues));
        await applyUnifiedPrequalApplication(castValues);
      } catch (e) {
        if (catchOnly(e, NonClientError, PrequalificationApplicationError)) {
          push('/credit-error');
        }
      }
    },
    [push, applyUnifiedPrequalApplication, dispatch, schema],
  );

  const application = useSelector(selectCreditApplication);
  const incomeInitialValue = pick(
    ['income', 'referralCode', 'storeNumber', 'intendedPurchaseAmount'],
    application,
  );
  const initialValues: FormValues = {
    ...incomeInitialValue,
    consent: false,
  } as FormValues;

  return (
    <FmcForm
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={schema}
    >
      {formProps => (
        <UnifiedPrequalApplicationReviewView
          emailAddress={application.emailAddress}
          {...formProps}
        />
      )}
    </FmcForm>
  );
};

export default UnifiedPrequalApplicationReview;
